import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAppDispatch } from "../../common/state/hooks";
import { useLoading } from "../../common/components/AppState/IsLoading";
import { LoadingState } from "../../common/state/slice/appStateSlice";
import {
  fetchProjectGroupsCompletion,
  getEnduserProjectGroupsCompletion,
} from "../../features/enduserStats/state/enduserStatsSlice";
import { useEnduserStatsCache } from "../../features/enduserStats/hooks";
import {
  SearchAndSort,
  SortOption,
  Pagination,
  EmptyState,
  SkeletonCard,
} from "../../features/statsNew/components";
import { DateRange } from "../../features/statsNew/components/DateRangePicker";
import SegmentedControl from "src/common/components/SegmentedControl/SegmentedControl";
import {
  useEnduserStatusOptions,
  type StatusFilter,
} from "../../features/enduserStats/components/enduserStatusOptions";
import EnduserParcoursStatusCard from "../../features/enduserStats/components/EnduserParcoursStatusCard";
import EnduserProgressionCard from "../../features/enduserStats/components/EnduserProgressionCard";
import EnduserScoreCard from "../../features/enduserStats/components/EnduserScoreCard";
import EnduserEngagementCard from "../../features/enduserStats/components/EnduserEngagementCard";

interface OutletContextType {
  dateRange: DateRange;
  clearDateRange: () => void;
}

const ENDUSER_PARCOURS_LIST_COMPONENT_ID = "enduserParcoursListView";

const EnduserParcoursListView = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { dateRange, clearDateRange } = useOutletContext<OutletContextType>();
  const projectGroupsCompletion = useSelector(getEnduserProjectGroupsCompletion);
  const loadingState = useLoading(ENDUSER_PARCOURS_LIST_COMPONENT_ID);
  const isLoading = loadingState === LoadingState.Loading;
  const { shouldRefetch, markAsFetched } = useEnduserStatsCache();

  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState("activity-desc");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);

  // Fetch only if cache is invalid (user changed or first load)
  useEffect(() => {
    if (shouldRefetch) {
      dispatch(
        fetchProjectGroupsCompletion({
          componentId: ENDUSER_PARCOURS_LIST_COMPONENT_ID,
          limit: -1,
        }),
      ).then(() => {
        markAsFetched();
      });
    }
  }, [dispatch, shouldRefetch, markAsFetched]);

  const sortOptions: SortOption[] = [
    { key: "activity-desc", label: t("statsNew.sort.activityRecent") },
    { key: "activity-asc", label: t("statsNew.sort.activityOldest") },
    { key: "name-asc", label: t("statsNew.sort.nameAZ") },
    { key: "name-desc", label: t("statsNew.sort.nameZA") },
  ];

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let result = projectGroupsCompletion;

    // Filter by date range (based on last session date)
    if (dateRange.startDate || dateRange.endDate) {
      result = result.filter((pg) => {
        if (!pg.dateLastSession) return false;
        const lastSessionDate = new Date(pg.dateLastSession);
        if (dateRange.startDate && lastSessionDate < dateRange.startDate) return false;
        if (dateRange.endDate && lastSessionDate > dateRange.endDate) return false;
        return true;
      });
    }

    // Filter by status using progressPercentageOnLatestBuild
    if (statusFilter !== "all") {
      result = result.filter((pg) => {
        const progress = Math.round(pg.progressPercentageOnLatestBuild ?? 0);

        if (statusFilter === "completed") {
          return progress === 100;
        }
        if (statusFilter === "inProgress") {
          return progress > 0 && progress < 100;
        }
        if (statusFilter === "notStarted") {
          return progress === 0;
        }
        return true;
      });
    }

    // Filter by search
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter((pg) => pg.projectGroupName?.toLowerCase().includes(search));
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortKey) {
        case "name-asc":
          return (a.projectGroupName ?? "").localeCompare(b.projectGroupName ?? "");
        case "name-desc":
          return (b.projectGroupName ?? "").localeCompare(a.projectGroupName ?? "");
        case "activity-desc": {
          // Sort by latest session date (most recent first)
          const aDate = a.dateLastSession ? new Date(a.dateLastSession).getTime() : 0;
          const bDate = b.dateLastSession ? new Date(b.dateLastSession).getTime() : 0;
          return bDate - aDate;
        }
        case "activity-asc": {
          const aDateAsc = a.dateLastSession ? new Date(a.dateLastSession).getTime() : 0;
          const bDateAsc = b.dateLastSession ? new Date(b.dateLastSession).getTime() : 0;
          return aDateAsc - bDateAsc;
        }
        default:
          return 0;
      }
    });

    return result;
  }, [projectGroupsCompletion, dateRange, statusFilter, searchValue, sortKey]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchValue, statusFilter, sortKey, dateRange]);

  // Paginate the data
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredAndSortedData.slice(start, start + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleParcoursClick = (parcoursId: number) => {
    navigate(`/stats/parcours/${parcoursId}`);
  };

  // Filter parcours by date for status counts
  const dateFilteredParcours = useMemo(() => {
    if (!dateRange.startDate && !dateRange.endDate) {
      return projectGroupsCompletion;
    }
    return projectGroupsCompletion.filter((pg) => {
      if (!pg.dateLastSession) return false;
      const lastSessionDate = new Date(pg.dateLastSession);
      if (dateRange.startDate && lastSessionDate < dateRange.startDate) return false;
      if (dateRange.endDate && lastSessionDate > dateRange.endDate) return false;
      return true;
    });
  }, [projectGroupsCompletion, dateRange]);

  // Calculate status counts using progressPercentageOnLatestBuild (from date-filtered data)
  const statusCounts = useMemo(() => {
    const counts = { all: 0, completed: 0, inProgress: 0, notStarted: 0 };
    dateFilteredParcours.forEach((pg) => {
      counts.all++;
      const progress = Math.round(pg.progressPercentageOnLatestBuild ?? 0);

      if (progress === 100) {
        counts.completed++;
      } else if (progress > 0) {
        counts.inProgress++;
      } else {
        counts.notStarted++;
      }
    });
    return counts;
  }, [dateFilteredParcours]);

  const statusOptions = useEnduserStatusOptions(statusCounts, statusFilter);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-64 bg-gray-200 rounded-lg animate-pulse" />
        <div className="flex flex-wrap gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} variant="parcours" />
          ))}
        </div>
      </div>
    );
  }

  // Check if date filter is active
  const hasDateFilter = dateRange.startDate !== null || dateRange.endDate !== null;

  const renderEmptyState = () => {
    if (searchValue) {
      return (
        <EmptyState
          icon="search"
          title={t("statsNew.emptyState.noSearchResults")}
          description={t("statsNew.emptyState.noSearchResultsDescription")}
        />
      );
    }

    // No results due to date range filter (but there are parcours without filter)
    if (hasDateFilter && projectGroupsCompletion.length > 0) {
      return (
        <EmptyState
          icon="calendar"
          title={t("statsNew.emptyState.noData")}
          description={t("statsNew.emptyState.noDataDescription")}
          action={{
            label: t("statsNew.emptyState.viewAll"),
            onClick: clearDateRange,
          }}
        />
      );
    }

    // Status-specific empty states
    if (statusFilter === "notStarted") {
      return (
        <EmptyState
          icon="folder"
          title={t("enduserStats.parcours.noParcoursNotStarted")}
          description={t("enduserStats.parcours.noParcoursNotStartedDescription")}
        />
      );
    }

    if (statusFilter === "inProgress") {
      return (
        <EmptyState
          icon="folder"
          title={t("enduserStats.parcours.noParcoursInProgress")}
          description={t("enduserStats.parcours.noParcoursInProgressDescription")}
        />
      );
    }

    if (statusFilter === "completed") {
      return (
        <EmptyState
          icon="folder"
          title={t("enduserStats.parcours.noParcoursCompleted")}
          description={t("enduserStats.parcours.noParcoursCompletedDescription")}
        />
      );
    }

    return (
      <EmptyState
        icon="folder"
        title={t("enduserStats.parcours.noParcours")}
        description={t("enduserStats.parcours.noParcoursDescription")}
      />
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Hero Cards Section */}
      <div className="flex-shrink-0 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <EnduserProgressionCard parcours={projectGroupsCompletion} />
          <EnduserScoreCard parcours={projectGroupsCompletion} />
          <EnduserEngagementCard parcours={projectGroupsCompletion} />
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex-shrink-0 mb-3">
        <SegmentedControl<StatusFilter>
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </div>

      {/* Search and Sort */}
      <div className="flex-shrink-0 mb-3">
        <SearchAndSort
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={t("statsNew.parcours.searchPlaceholder")}
          sortOptions={sortOptions}
          selectedSort={sortKey}
          onSortChange={setSortKey}
        />
      </div>

      {/* Parcours Grid */}
      {filteredAndSortedData.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="flex flex-wrap gap-3">
              {paginatedData.map((parcours) => (
                <EnduserParcoursStatusCard
                  key={parcours.projectGroupId}
                  parcours={parcours}
                  onClick={() => handleParcoursClick(parcours.projectGroupId)}
                />
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex-shrink-0 mt-auto">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredAndSortedData.length}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EnduserParcoursListView;
